import { FC, Fragment, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { selectIsEnterpriseUser } from '../../../../../selectors/user';
import PaperModalV2 from 'raydiant-elements/core/PaperModalV2';
import Text from '@raydiant/raydial/components/Text';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import Button from '@raydiant/raydial/components/Button';
import Spinner from '@raydiant/raydial/components/Spinner';
import MoveContentTree from './MoveContentTree';
import analytics from '../../../../../clients/analyticsClient';
import * as paths from '../../../../../routes/paths';

export interface MoveContentProps {
  disabledFolderIds: string[];
  onMove: (folderId: string | null) => Promise<void>;
  initialScope?: string;
  // 'duplicate_and_move' locks the destination to Shared Libraries and changes
  // labels so the picker reads as a duplicate-here action.
  mode?: 'move' | 'duplicate_and_move';
}

type Crumb = { id: string; name: string };

const MoveContent: FC<MoveContentProps> = ({
  disabledFolderIds,
  onMove,
  initialScope = 'owned',
  mode = 'move',
}) => {
  const isDuplicateAndMove = mode === 'duplicate_and_move';

  const [scope, setScope] = useState(initialScope);
  // Breadcrumb stack: empty = at root of current scope; otherwise the deepest
  // entry is the current location.
  const [pathStack, setPathStack] = useState<Crumb[]>([]);
  const [isMoving, setIsMoving] = useState(false);

  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);

  // Used to detect when the user is browsing the library they currently have
  // open, so we can disable picking the same location they're sitting in.
  const myLibraryMatch = useRouteMatch({
    path: paths.library.pattern,
    exact: true,
  });
  const sharedLibrariesMatch = useRouteMatch({
    path: paths.librarySharedLibraries.pattern,
  });
  const location = useLocation();
  const urlFolderId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('folder_id') ?? '';
  }, [location.search]);

  // Plain Move stays within the source scope: a My Library item can only be
  // moved within My Library, a Shared Library item only within Shared
  // Libraries. The cross-scope promotion from My Library to a Shared Library
  // is handled by Duplicate and Move, not by plain Move.
  const canTargetSharedLibraries =
    isEnterpriseUser &&
    (isDuplicateAndMove || initialScope === 'shared_library');
  const canTargetMyLibrary = !isDuplicateAndMove && initialScope === 'owned';
  const hasMultipleScopeOptions =
    [canTargetMyLibrary, canTargetSharedLibraries].filter(Boolean).length > 1;

  useEffect(() => {
    // Switching scope invalidates the current path.
    setPathStack([]);
  }, [scope]);

  const isSharedLibrariesScope = scope === 'shared_library';
  const rootLabel = isSharedLibrariesScope ? 'Shared Libraries' : 'My Library';

  const currentFolderId = pathStack[pathStack.length - 1]?.id ?? '';
  const isAtRoot = pathStack.length === 0;

  // The destination is invalid when:
  //  - we're at the root of the Shared Libraries scope (can't drop at the list
  //    of shared libraries; must be inside one),
  //  - the destination folder is one of the items being moved,
  //  - the destination is the same place the source already lives.
  const isSamePlaceAsSource = isAtRoot
    ? (scope === 'owned' && !!myLibraryMatch && urlFolderId === '') ||
      (scope === 'shared_library' &&
        !!sharedLibrariesMatch &&
        urlFolderId === '')
    : currentFolderId === urlFolderId;
  const canMoveHere =
    !(isAtRoot && isSharedLibrariesScope) &&
    !disabledFolderIds.includes(currentFolderId) &&
    !isSamePlaceAsSource;

  const handleScopeChange = (key: string) => {
    setScope(key);
    // pathStack reset is handled by the effect on `scope`.
  };

  const handleNavigateInto = (folder: Crumb) => {
    setPathStack((prev) => [...prev, folder]);
  };

  // `crumbIndex` is -1 for the root crumb, 0..n-1 for entries in pathStack.
  const handleBreadcrumbClick = (crumbIndex: number) => {
    setPathStack((prev) => prev.slice(0, crumbIndex + 1));
  };

  const handleAction = async () => {
    if (!canMoveHere) return;
    setIsMoving(true);
    try {
      await onMove(currentFolderId || null);
      const contentCount = disabledFolderIds.length || 1;
      const targetFolderId = currentFolderId || null;
      // Always emit the generic move/copy event; keep the legacy shared-library
      // event so existing dashboards keep working.
      if (isDuplicateAndMove) {
        analytics.trackLibraryCopyHereClicked({
          sourceScope: initialScope,
          targetScope: scope,
          contentCount,
          targetFolderId,
        });
      } else {
        analytics.trackLibraryMoveHereClicked({
          sourceScope: initialScope,
          targetScope: scope,
          contentCount,
          targetFolderId,
        });
        if (scope === 'shared_library') {
          analytics.trackContentMovedToSharedLibrary({
            sourceScope: initialScope,
            targetScope: scope,
            contentCount,
          });
        }
      }
    } finally {
      setIsMoving(false);
    }
  };

  const actionButtonLabel = isDuplicateAndMove ? 'Copy here' : 'Move here';

  return (
    <PaperModalV2.Body className="flex flex-col gap-1 flex-1 min-h-0 !pb-2">
      <div>
        <Text variant="subheader" className="text-black flex">
          {isDuplicateAndMove
            ? 'Select Shared Library Folder'
            : 'Select Folder'}
        </Text>
      </div>
      {hasMultipleScopeOptions && (
        <div>
          <Select
            label="Library"
            className="w-full"
            selectedKey={scope}
            onSelectionChange={(e) => handleScopeChange(e as string)}
          >
            {canTargetMyLibrary && (
              <SelectItem id="owned">My Library</SelectItem>
            )}
            {canTargetSharedLibraries && (
              <SelectItem id="shared_library">Shared Libraries</SelectItem>
            )}
          </Select>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center flex-wrap gap-1 text-sm py-2 pl-3">
        <button
          type="button"
          onClick={() => handleBreadcrumbClick(-1)}
          className={
            isAtRoot
              ? 'text-black font-semibold'
              : 'text-blue-600 hover:underline'
          }
          disabled={isAtRoot}
        >
          {rootLabel}
        </button>
        {pathStack.map((crumb, i) => {
          const isLast = i === pathStack.length - 1;
          return (
            <Fragment key={crumb.id}>
              <span className="text-gray-400">/</span>
              <button
                type="button"
                onClick={() => handleBreadcrumbClick(i)}
                className={
                  isLast
                    ? 'text-black font-semibold'
                    : 'text-blue-600 hover:underline'
                }
                disabled={isLast}
              >
                {crumb.name}
              </button>
            </Fragment>
          );
        })}
      </div>

      <div className="flex flex-col flex-1 overflow-auto">
        <MoveContentTree
          scope={scope}
          folderId={currentFolderId}
          onNavigate={handleNavigateInto}
          disabledFolderIds={disabledFolderIds}
          isSharedLibraryRoot={isSharedLibrariesScope && isAtRoot}
        />
      </div>

      <div className="mt-auto flex items-center justify-start py-4 shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.1)]">
        <Button
          className="h-11 px-4 py-3 rounded-2xl bg-blue-600 disabled:bg-gray-600"
          aria-label={actionButtonLabel}
          variant="primary"
          isDisabled={!canMoveHere || isMoving}
          onPress={handleAction}
        >
          <div className="flex text-white items-center">
            {isMoving ? (
              <Spinner size="small" />
            ) : (
              <Text className="normal-case" variant="smallSubtitle">
                {actionButtonLabel}
              </Text>
            )}
          </div>
        </Button>
      </div>
    </PaperModalV2.Body>
  );
};

export default MoveContent;
